import { useCallback, useMemo } from 'react';

import { ChannelIcon } from 'src/components/ChannelIcon';
import { doesAssetSupportPublishing } from 'src/data/asset';
import { CHANNEL, getChannelLabel } from 'src/data/channel';
import { useFreshRef } from 'src/lib/hooks';
import { useChannelAccounts, useConnectToChannel } from 'src/services';
import { useCustomerPublishIntegrationsEnabled } from 'src/services/customer';
import { useNotificationActions } from 'src/store';

import { useExportAssetNamePopup } from '../components/ExportAssetNamePopup';
import { useListViewActions } from '../store';

import { useListViewEvents } from './useListViewEvents';

const AVAILABLE_CHANNELS = [CHANNEL.salesforceExport];

export const useExportMenuItems = ({ generationTool, variations }) => {
  const publishIntegrationsEnabled = useCustomerPublishIntegrationsEnabled();
  const { connectedAccounts } = useChannelAccounts();
  const variationsRef = useFreshRef(variations);
  const handleExport = useExportHandler();

  const variationsSupported = useMemo(() => checkVariationsSupported(variations), [variations]);
  const exportAllowed = publishIntegrationsEnabled && variationsSupported;

  return useMemo(() => {
    // Do not display any export options if the feature is not enabled for this customer or we have unsupported variations
    if (!exportAllowed) {
      return [];
    }

    return AVAILABLE_CHANNELS.map((channel) => {
      // If we have connected accounts, use the first one (for now)
      const channelAccounts = connectedAccounts[channel] || [];
      const account = channelAccounts.length > 0 ? channelAccounts[0] : null;

      return {
        id: channel,
        label: getMenuItemLabel({ channel, account }),
        icon: <ChannelIcon channel={channel} />,
        onClick: () =>
          handleExport({
            generationTool,
            channel,
            account,
            variations: variationsRef.current,
          }),
      };
    });
  }, [exportAllowed, connectedAccounts, handleExport, generationTool, variationsRef]);
};

const useExportHandler = () => {
  const selectChannelAccount = useSelectChannelAccount();
  const { openExportAssetNamePopup } = useExportAssetNamePopup();
  const notifications = useNotificationActions();
  const listViewActions = useListViewActions();
  const { trackInitiatedPublishFlow } = useListViewEvents();

  return useCallback(
    async ({ generationTool, channel, account, variations }) => {
      const variationIds = variations.map((v) => v.id);

      trackInitiatedPublishFlow({
        generationTool,
        channel,
        variationIds,
      });

      // If we don't have account selected, request it from user
      if (!account) {
        account = await selectChannelAccount(channel);
        if (!account) {
          return;
        }
      }

      // Request user to enter "asset name" for the export
      const assetName = await openExportAssetNamePopup();
      if (!assetName) {
        return;
      }

      try {
        // Publish current variation to the selected account
        await listViewActions.publishVariations({
          variationIds,
          dataSource: channel,
          accountId: account.id,
          assetName,
        });

        const message = formatSuccessMessage({ channel });
        notifications.displaySuccess(message);
      } catch (error) {
        notifications.displayError(error);
      }
    },
    [
      trackInitiatedPublishFlow,
      openExportAssetNamePopup,
      selectChannelAccount,
      listViewActions,
      notifications,
    ]
  );
};

const useSelectChannelAccount = () => {
  const { connectToChannel } = useConnectToChannel();

  return useCallback(
    (channel) => {
      return new Promise((resolve) => {
        connectToChannel({
          channel,
          onConnectSuccess: ({ account }) => {
            resolve(account);
          },
        });
      });
    },
    [connectToChannel]
  );
};

const checkVariationsSupported = (variations) =>
  variations.every((v) => doesAssetSupportPublishing(v.assetType));

const formatSuccessMessage = ({ channel }) => {
  switch (channel) {
    case CHANNEL.salesforceExport:
      return 'Copy exported to: “Anyword” folder in your Salesforce Marketing Cloud account.';
    default:
      return 'Copy exported successfully';
  }
};

const getMenuItemLabel = ({ channel, account }) => {
  if (account) {
    return (
      <span>
        {account.name} <i>({getChannelLabel(channel)})</i>
      </span>
    );
  }

  return <span>{getChannelLabel(channel)}</span>;
};
